// Relative import on purpose: vitest.config.ts does not alias "@".
import { toArabicNumerals } from "../arabicNumerals";

// Deterministic Arabic formatting for blog furniture (dates, reading time, heading anchors,
// DataBlock values). Everything here is a pure function of its input: no Date parsing in local
// time, no locale-dependent date APIs, no Intl. The same string renders on the SSR pass and on hydration,
// whatever the server's or the browser's locale and timezone.

const ARABIC_MONTHS = [
  "يناير",
  "فبراير",
  "مارس",
  "أبريل",
  "مايو",
  "يونيو",
  "يوليو",
  "أغسطس",
  "سبتمبر",
  "أكتوبر",
  "نوفمبر",
  "ديسمبر",
] as const;

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const isLeapYear = (year: number): boolean =>
  (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

const daysInMonth = (year: number, month: number): number =>
  month === 2 ? (isLeapYear(year) ? 29 : 28) : [4, 6, 9, 11].includes(month) ? 30 : 31;

/**
 * "2026-10-24" → «٢٤ أكتوبر ٢٠٢٦». Parses the string by splitting, never through the Date constructor.
 * Throws on anything that is not a real YYYY-MM-DD date: the input is build-time frontmatter or
 * snapshot data, so a bad value must fail the build loudly rather than render garbage.
 */
export const formatArabicDate = (isoDate: string): string => {
  const match = ISO_DATE_RE.exec(isoDate ?? "");
  if (!match) throw new Error(`formatArabicDate: expected YYYY-MM-DD, got "${isoDate}"`);
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) {
    throw new Error(`formatArabicDate: invalid date "${isoDate}"`);
  }
  return `${toArabicNumerals(day)} ${ARABIC_MONTHS[month - 1]} ${toArabicNumerals(year)}`;
};

// ─── Reading time ───

const WORDS_PER_MINUTE = 200;

/** Words in an MDX body, with frontmatter, JSX tags and markdown syntax characters removed. */
const countWords = (text: string): number => {
  const stripped = (text ?? "")
    .replace(/^---\r?\n[\s\S]*?\r?\n---(\r?\n|$)/, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/[#*_>`|~[\](){}!\-=]/g, " ");
  return stripped.split(/\s+/).filter((token) => /[\p{L}\p{N}]/u.test(token)).length;
};

/** ceil(words / 200), minimum 1 (UI-SPEC reading time). */
export const readingTimeMinutes = (text: string): number =>
  Math.max(1, Math.ceil(countWords(text) / WORDS_PER_MINUTE));

/** Arabic count agreement: 1 «دقيقة», 2 «دقيقتين», 3-10 «n دقايق», 11+ «n دقيقة». */
export const readingTimeLabel = (minutes: number): string => {
  if (minutes <= 1) return "دقيقة قراءة";
  if (minutes === 2) return "دقيقتين قراءة";
  if (minutes <= 10) return `${toArabicNumerals(minutes)} دقايق قراءة`;
  return `${toArabicNumerals(minutes)} دقيقة قراءة`;
};

// ─── Headings / TOC ───

/**
 * Stable anchor id for an h2. Used by BOTH the TOC (from raw `## ` lines in +data.ts) and the MDX
 * h2 override, so the two always agree. Keeps letters and digits in any script (Arabic included),
 * lowercases Latin, drops everything else and joins the words with "-".
 */
export const headingId = (text: string): string =>
  (text ?? "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .join("-");

export type BlogHeading = { text: string; id: string };

/** `## ` lines of a raw MDX file, skipping the frontmatter block and fenced code. */
export const extractH2Headings = (raw: string): BlogHeading[] => {
  const lines = (raw ?? "").split(/\r?\n/);
  const headings: BlogHeading[] = [];
  let start = 0;

  if (lines[0]?.trim() === "---") {
    const end = lines.findIndex((line, index) => index > 0 && line.trim() === "---");
    if (end > 0) start = end + 1;
  }

  let inFence = false;
  for (const line of lines.slice(start)) {
    if (/^\s*(```|~~~)/.test(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    const match = /^## (.+)$/.exec(line);
    if (!match) continue;
    const text = match[1].trim();
    if (text) headings.push({ text, id: headingId(text) });
  }
  return headings;
};

// ─── DataBlock values ───

/**
 * One thousands style everywhere (UI-SPEC Digits): NO separator, «١٦٨٢». Decimal separator «٫»
 * (U+066B), percent sign «٪».
 */
export const formatBlockValue = (value: number, unit: "count" | "percent"): string => {
  const digits = toArabicNumerals(String(value).replace(".", "٫"));
  return unit === "percent" ? `${digits}٪` : digits;
};
